import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import { sanitizeUrl } from '@braintree/sanitize-url';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { createSpacingFields } from '../fields/spacing-fields';
import { interpolateFromPuck } from '../interpolation';
import {
  BRAND_COLOR,
  BRAND_CONTRAST_COLOR,
  BUTTON_FONT_WEIGHT,
  BUTTON_INSET,
  BUTTON_RADIUS,
  lookup,
  NO_SPACING,
  spacingToCss,
} from '../fields/tokens';
import type { BlockWidthOption, BoxSpacing } from '../fields/tokens';

type TextAlign = 'center' | 'left' | 'right';

/**
 * Visual treatments, all derived from the merchant's configured buttons/links colour so
 * a CMS button matches the rest of their storefront.
 */
const BUTTON_STYLES = {
  primary: {
    background: BRAND_COLOR,
    color: BRAND_CONTRAST_COLOR,
    border: 'none',
  },
  secondary: {
    background: 'transparent',
    color: BRAND_COLOR,
    border: `2px solid ${BRAND_COLOR}`,
  },
  tertiary: {
    background: 'transparent',
    color: BRAND_COLOR,
    border: 'none',
  },
} as const;

type ButtonStyle = keyof typeof BUTTON_STYLES;

export type ButtonProps = {
  label: string;
  href: string;
  openInNewTab: 'no' | 'yes';
  buttonStyle: ButtonStyle;
  desktopWidth: BlockWidthOption;
  mobileWidth: BlockWidthOption;
  align: TextAlign;
  padding: BoxSpacing;
};

const Wrapper = styled.div`
  text-align: var(--cms-button-align, left);
  padding: var(--cms-button-padding, 0px);
`;

/**
 * `fill` has to apply to the anchor itself rather than the wrapper, since an inline-block
 * anchor ignores a wrapper's width. The mobile value falls back to the desktop one.
 */
const StyledButton = styled.a`
  display: inline-block;
  box-sizing: border-box;
  width: var(--cms-button-width, auto);
  padding: ${BUTTON_INSET};
  border-radius: ${BUTTON_RADIUS};
  font-size: var(--cms-button-font-size, 16px);
  font-weight: ${BUTTON_FONT_WEIGHT};
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  background: var(--cms-button-background, ${BUTTON_STYLES.primary.background});
  color: var(--cms-button-color, ${BUTTON_STYLES.primary.color});
  border: var(--cms-button-border, none);

  @media ${mediaQueries.largePhone} {
    width: var(--cms-button-mobile-width, var(--cms-button-width, auto));
  }
`;

const SPACING_FIELDS = createSpacingFields();

/** `fill` needs a percentage on the anchor; `fit` lets content size it. */
const ANCHOR_WIDTHS = { fit: 'auto', fill: '100%' } as const;

const WIDTH_OPTIONS = [
  { label: 'Fit', value: 'fit' },
  { label: 'Fill', value: 'fill' },
];

export const buttonConfig: ComponentConfig<ButtonProps> = {
  label: 'Button',
  ai: {
    instructions:
      'A call-to-action link styled as a button. Use for navigation, signups, and primary actions. Colour comes from the merchant theme, so pick a style rather than a colour.',
  },
  fields: {
    label: { type: 'text', label: 'Label' },
    href: {
      type: 'text',
      label: 'Link URL',
      placeholder: '/shop',
      ai: { stream: false, instructions: 'Full URL or relative path for the button link.' },
    },
    openInNewTab: {
      type: 'radio',
      label: 'Open in new tab',
      options: [
        { label: 'No', value: 'no' },
        { label: 'Yes', value: 'yes' },
      ],
      ai: { schema: { type: 'string', enum: ['no', 'yes'] } },
    },
    buttonStyle: {
      type: 'select',
      label: 'Style',
      options: [
        { label: 'Primary', value: 'primary' },
        { label: 'Secondary', value: 'secondary' },
        { label: 'Tertiary', value: 'tertiary' },
      ],
      ai: {
        schema: { type: 'string', enum: ['primary', 'secondary', 'tertiary'] },
        instructions:
          'primary = filled with the brand colour, secondary = outlined, tertiary = text only. Use one primary button per page section.',
      },
    },
    desktopWidth: {
      type: 'radio',
      label: 'Desktop width',
      options: WIDTH_OPTIONS,
      ai: { schema: { type: 'string', enum: ['fit', 'fill'] } },
    },
    mobileWidth: {
      type: 'radio',
      label: 'Mobile width',
      options: WIDTH_OPTIONS,
      ai: {
        schema: { type: 'string', enum: ['fit', 'fill'] },
        instructions: 'Full-width buttons are usually easier to tap on phones.',
      },
    },
    align: {
      type: 'radio',
      label: 'Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
      ai: { schema: { type: 'string', enum: ['left', 'center', 'right'] } },
    },
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: SPACING_FIELDS,
    },
  },
  defaultProps: {
    label: 'Click me',
    href: '#',
    openInNewTab: 'no',
    buttonStyle: 'primary',
    desktopWidth: 'fit',
    mobileWidth: 'fill',
    align: 'left',
    padding: NO_SPACING,
  },
  render: ({
    label = 'Click me',
    href = '#',
    openInNewTab = 'no',
    buttonStyle = 'primary',
    desktopWidth = 'fit',
    mobileWidth = 'fill',
    align = 'left',
    padding,
    puck,
  }) => {
    const treatment = lookup(BUTTON_STYLES, buttonStyle, BUTTON_STYLES.primary);
    const newTab = openInNewTab === 'yes';
    const resolvedLabel = interpolateFromPuck(label, puck);

    return (
      <Wrapper
        style={
          {
            '--cms-button-align': align,
            '--cms-button-padding': spacingToCss(padding),
          } as React.CSSProperties
        }
      >
        <StyledButton
          href={sanitizeUrl(href)}
          target={newTab ? '_blank' : undefined}
          // noopener/noreferrer: a new-tab link would otherwise hand the destination a
          // window.opener handle back into the storefront.
          rel={newTab ? 'noopener noreferrer' : undefined}
          style={
            {
              '--cms-button-width': lookup(ANCHOR_WIDTHS, desktopWidth, ANCHOR_WIDTHS.fit),
              '--cms-button-mobile-width': lookup(ANCHOR_WIDTHS, mobileWidth, ANCHOR_WIDTHS.fill),
              '--cms-button-background': treatment.background,
              '--cms-button-color': treatment.color,
              '--cms-button-border': treatment.border,
            } as React.CSSProperties
          }
        >
          {resolvedLabel}
        </StyledButton>
      </Wrapper>
    );
  },
};
